import React, { useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { TableColumn } from 'tea-component'
import { getEchartTooltipText } from '@/pages/dashboard/components/EchartsBubble'
import { commonDownloadValueFormatter, commonValueFormatter, formatExeclCommon, formatFilterValues, formatNum, getWeekDayTitle, handleDownload } from '@/pages/dashboard/utils/utils'
import { useLocal, useOption, useWeekDay, useWeekDayArr } from '@/pages/dashboard/utils/customHook'
import CardBox from '@/pages/dashboard/components/CardBox'
import { xAxisHighLight } from '@/pages/dashboard/components/Chart/_config/plug'
import LineChart from '@/pages/dashboard/components/Chart/components/LineChart'
import MiniTable from '@/pages/dashboard/components/MiniTable'
import { useIntl, useSelector } from 'umi'
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context'
import { getApiData } from '@/pages/dashboard/services/common'

const labelFormatX = (value) => (moment(value.split("(")[0]).format('MM-DD'))
// 格式化横坐标 label
const axisLabelFormat = (opt) => {
    return opt.xAxis?.map((item) => {
        return {
            ...item,
            axisLabel: {
                ...item.axisLabel,
                formatter: labelFormatX,
            },
            // axisTick: {
            //   alignWithLabel: true, // x轴刻度线是否紧靠0
            //   // interval:1
            // },
        }
    })
}

function AveragePlaytimeByLevelCo(props: any) {
    const { config: propsConfig, className, style } = props;
    const { api, componentMetrics } = propsConfig || {}
    const { type = 'int', decimal = 0, enName, cnName } = componentMetrics || {}
    const contextObj = useContext(FilterContext)
    const { filterVal, filterLoading } = contextObj || {};
    const { gameCode } = useSelector((state: any) => {
        return {
            gameCode: state?.dashboard?.currentGame?.game_code,
        }
    });
    const { getWeekDay } = useWeekDay()
    const weekDayArr = useWeekDayArr();
    const { formatMessage } = useIntl() // 中英切换方法
    const { isEN, locale, getLocalText } = useLocal('engagement_active_online_level')
    const [averageOnlineTimeByLevelData, setAverageOnlineTimeByLevelData] = useState([])
    const [averageOnlineTimeByLevelLoading, setAverageOnlineTimeByLevelLoading] = useState(true)
    const [error, setError] = useState(false)
    const [ordersColumn, setOrdersColumn] = useState<string[]>([])

    const formatterFunc = (params) => {
        return getEchartTooltipText({
            title: `${params[0].axisValue}`,
            data: params.map(({ seriesName, value, color }) => ({
                name: seriesName,
                metricsNum: commonValueFormatter(type, value, decimal),
                color,
            })),
        })
    }

    const config = {
        line: {
            formatterFunc,
            setOtherConfigFunc: (opt) => {
                opt.legend.left = 'center'
                opt.legend.bottom = 0
                opt.xAxis = axisLabelFormat(opt)
                opt.series = opt.series?.map((item) => {
                    return {
                        ...item,
                    }
                })
                return opt
            },
        },
        table: {
            isSortable: true,
            setSortableConfig: (sorts, setSorts) => {
                return {
                    columns: [
                        {
                            key: 'date',
                            prefer: 'desc',
                            sorter: (first, second) => {
                                if (+moment(first.date) > +moment(second.date)) return 1
                                if (+moment(first.date) < +moment(second.date)) return -1
                                return 0
                            },
                        },
                        ...ordersColumn?.map((key) => {
                            return {
                                key,
                                prefer: 'desc',
                                sorter: (first, second) => first[key].replace(/[,-]/g, '') - second[key].replace(/[,-]/g, ''),
                            }
                        }),
                    ],
                    value: sorts,
                    onChange: (value, context) => setSorts(value.length ? [context.sort] : []),
                }
            },
        },
    }

    useEffect(() => {
        if (!filterVal) return;
        !filterLoading && getData();
    }, [filterVal, filterLoading]);

    const getData = () => {
        const { rangeTime, diff, timeType, ...filters } = filterVal || {};
        const params = {
            game_code: gameCode,
            start_time: rangeTime?.[0] || '',
            end_time: rangeTime?.[1] || '',
            filters: formatFilterValues(filters),
        }
        setAverageOnlineTimeByLevelLoading(true)
        getApiData(api, params).then((res: any) => {
            setAverageOnlineTimeByLevelLoading(false);
            setError(false);
            setAverageOnlineTimeByLevelData(res ?? [])
        })
            .catch((err: any) => {
                setError(true);
                setAverageOnlineTimeByLevelLoading(false);
                setAverageOnlineTimeByLevelData([])
            });
    }

    const option = useMemo(() => {
        const data_list = averageOnlineTimeByLevelData
        // 图表数组
        const chart: any[] = []
        data_list?.forEach((cur) => {
            let { date, level_list = [] } = cur
            level_list?.forEach((item) => {
                const { level, value } = item
                const it = chart.find((el) => el.name === level)
                if (!it) {
                    chart.push({
                        name: level,
                        // type: 'bar',
                        // stack: 'stack',
                        data: [
                            {
                                x: getWeekDayTitle(date, weekDayArr),
                                y: value,
                            },
                        ],
                    })
                } else {
                    it?.data?.push({
                        x: getWeekDayTitle(date, weekDayArr),
                        y: value,
                    })
                }
            })
        })
        // 表格数据设置表头->取data_list列表的第0项数据的data_list
        const { level_list = [] } = data_list?.[0] || {}
        const setMap = new Set()
        level_list?.forEach(({ level }) => {
            setMap.add(level)
        })
        // 表头名称
        const headerNames = [...setMap]
        setOrdersColumn(headerNames as string[])
        // 表头固定列
        const tabbleOption: TableColumn[] = [
            {
                key: 'date',
                align: 'left',
                fixed: 'left',
                width: 160,
                header: getLocalText('date'),
                render: (cvm) => {
                    const weekDay = getWeekDay(cvm?.date)
                    const isSunOrSat = weekDay === 'Sun' || weekDay === 'Sat' || weekDay === '周日' || weekDay === '周六'
                    return <>
                        <span className='table-text'>{cvm?.date}</span>
                        {filterVal?.timeType !== 'Month' && <span className={isSunOrSat ? 'text-week-b' : 'text-week'}>{weekDay}</span>}
                    </>
                },
            },
        ]
        // 表头非固定列
        headerNames?.reduce((pre: any, level: any) => {
            pre.push({
                key: level,
                align: 'right',
                width: 150,
                header: level,
                render: (cvm) => <span>{cvm[level]}</span>,
            })
            return pre
        }, tabbleOption)
        // 表数据
        const tabbleData = data_list?.reduce((outPre, outCur) => {
            const { date, level_list = [] } = outCur
            // row所对应的所有的column数据level->value
            const columnObj = level_list?.reduce((pre, { level, value }) => {
                pre[level] = commonValueFormatter(type, value, decimal)
                return pre
            }, {} as any)
            outPre.push({
                date: date,
                ...columnObj,
            })
            return outPre
        }, [] as any[])
        const tabbleDownloadData = data_list?.reduce((outPre, outCur) => {
            const { date, level_list = [] } = outCur
            // row所对应的所有的column数据level->value
            const columnObj = level_list?.reduce((pre, { level, value }) => {
                pre[level] = commonDownloadValueFormatter(type, value, decimal)
                return pre
            }, {} as any)
            outPre.push({
                date: date,
                ...columnObj,
            })
            return outPre
        }, [] as any[])
        return {
            chart,
            table: {
                option: tabbleOption,
                data: tabbleData,
                downloadData: tabbleDownloadData,
            },
        }
    }, [locale, averageOnlineTimeByLevelData])

    const { xAxisData, legendData, lineSeries, isNodata } = useOption(option)
    const renderList = [
        {
            type: 'lineChart',
            svgType: 'discount-chart',
            content: formatMessage({ id: 'common_line_chart' }),
            component: <LineChart
                height={340}
                legendData={xAxisData.length > 0 ? legendData : []}
                xAxisData={xAxisData || []}
                series={xAxisData.length > 0 ? lineSeries : []}
                formatterFunc={config?.line?.formatterFunc}
                setOtherConfigFunc={(opt: any) => {
                    if (config?.line?.setOtherConfigFunc) opt = config?.line?.setOtherConfigFunc(opt)
                    opt = xAxisHighLight(opt, {
                        formatter: (params: { value: string; }) => {
                            return labelFormatX(params?.value) ?? ''
                        }
                    })
                    return opt;
                }}
            />
        },
        {
            type: 'sheet',
            svgType: 'sheet',
            content: formatMessage({ id: 'common_table' }),
            component: <MiniTable
                height={340}
                loading={false}
                tableConfig={config?.table}
                tableOption={option?.table?.option}
                tableList={option?.table?.data}
            />
        },
    ]

    const title = isEN ? enName : cnName
    return (
        <div className={className} style={style}>
            <CardBox
                className='register-table'
                height={340}
                bordered={false}
                titleCom={title}
                renderList={renderList}
                defaultAvtive={0}
                downloadClick={async () => {
                    handleDownload(option?.table, title)
                }}
                isLoading={averageOnlineTimeByLevelLoading}
                isError={error}
                isNodata={isNodata}
            />
        </div>
    )

}

export default AveragePlaytimeByLevelCo
